import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

interface UseStickyOptimizedOptions {
  /**
   * Offset from top when in sticky mode (in pixels)
   * @default 16
   */
  stickyOffset?: number;
  /**
   * Fixed width to apply when in sticky mode (in pixels)
   * If not provided, will use the natural width of the element
   */
  fixedWidth?: number;
  /**
   * Minimum screen width for sticky behavior to activate
   * @default 768
   */
  minScreenWidth?: number;
  /**
   * Whether to enable the sticky behavior
   * @default true
   */
  enabled?: boolean;
  /**
   * The scrolling container selector (optional)
   * If not provided, uses window scroll (for regular pages)
   * If provided, uses that element's scroll (for modals, sidebars, etc.)
   */
  scrollContainer?: string | HTMLElement;
  /**
   * Maximum height for the sticky element (responsive to viewport)
   * @default 0.8 (80% of viewport height)
   */
  maxHeightRatio?: number;
  /**
   * Absolute maximum height in pixels
   * @default 600
   */
  maxHeightPx?: number;
  /**
   * Side to stick to - supports both left and right
   * @default 'left'
   */
  side?: 'left' | 'right';
  /**
   * Bottom spacing when element reaches the bottom of container (in pixels)
   * @default 24
   */
  bottomSpacing?: number;
}

type StickyState = 'static' | 'fixed' | 'bottom';

interface CachedDimensions {
  containerTop: number;
  containerBottom: number;
  containerLeft: number;
  containerRight: number;
  elementWidth: number;
  elementHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  timestamp: number;
}

/**
 * Optimized universal sticky hook - works for any context with performance improvements!
 *
 * Perfect for:
 * - Product images in modals (left side)
 * - Price sections (right side)
 * - Sidebar navigation
 * - Floating CTAs
 * - Any element that should stick within its container
 *
 * Performance Improvements:
 * - Memoized calculations to avoid duplicate work
 * - Cached dimensions with smart invalidation
 * - Throttled scroll handling with requestAnimationFrame
 * - Reduced DOM queries through caching
 *
 * Features:
 * - Three-state logic: static → fixed → bottom-anchored
 * - Works with window scroll OR custom scroll containers
 * - Responsive height calculations
 * - Smooth transitions with no flicker
 * - Proper cleanup and performance optimization
 * - Supports both left and right side positioning
 *
 * @example
 * // For regular page (window scroll) - left side
 * const { stickyRef, containerRef, isFixed } = useStickyOptimized();
 *
 * @example
 * // For modal (custom scroll container) - left side
 * const { stickyRef, containerRef, isFixed } = useStickyOptimized({
 *   scrollContainer: '#modal-wrapper',
 *   stickyOffset: 20
 * });
 *
 * @example
 * // For right side price section
 * const { stickyRef, containerRef, isFixed } = useStickyOptimized({
 *   side: 'right',
 *   stickyOffset: 16
 * });
 */
export function useStickyOptimized(options: UseStickyOptimizedOptions = {}) {
  const {
    stickyOffset = 16,
    fixedWidth,
    minScreenWidth = 768,
    enabled = true,
    scrollContainer,
    maxHeightRatio = 0.8,
    maxHeightPx = 600,
    side = 'left',
    bottomSpacing = 24,
  } = options;

  const stickyRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentState = useRef<StickyState>('static');
  const [isFixed, setIsFixed] = useState(false);

  // Simple client-side check without useEffect
  const isClient = typeof window !== 'undefined';

  // Cache for dimensions to avoid recalculating on every scroll
  const dimensionsCache = useRef<CachedDimensions | null>(null);
  const CACHE_DURATION = 250; // Cache dimensions for 250ms (reduced reflow frequency)

  // Memoized scroll element resolution - SSR safe
  const scrollElement = useMemo(() => {
    if (typeof window === 'undefined') return null;
    if (!scrollContainer) return window;
    if (typeof scrollContainer === 'string') {
      return document.querySelector(scrollContainer) || window;
    }
    return scrollContainer;
  }, [scrollContainer]);

  // Memoized max height calculation - SSR safe
  const maxHeight = useMemo(() => {
    if (typeof window === 'undefined') return maxHeightPx;
    const viewportHeight = window.innerHeight;
    const ratioHeight = viewportHeight * maxHeightRatio;
    return Math.min(ratioHeight, maxHeightPx);
  }, [maxHeightRatio, maxHeightPx]);

  // Optimized dimension calculation with caching
  const getDimensions = useCallback((): CachedDimensions | null => {
    // Don't run on server or before hydration
    if (!isClient || typeof window === 'undefined') return null;

    const now = Date.now();

    // Return cached dimensions if still valid
    if (dimensionsCache.current && now - dimensionsCache.current.timestamp < CACHE_DURATION) {
      return dimensionsCache.current;
    }

    if (!stickyRef.current || !containerRef.current) return null;

    const container = containerRef.current;
    const stickyElement = stickyRef.current;

    // Resolve scroll element fresh - match Embarc's approach
    let actualScrollElement: HTMLElement | Window;
    if (scrollContainer) {
      if (typeof scrollContainer === 'string') {
        const element = document.querySelector(scrollContainer) as HTMLElement;
        if (!element) return null; // Element not found yet
        actualScrollElement = element;
      } else {
        actualScrollElement = scrollContainer;
      }
    } else {
      actualScrollElement = window;
    }

    let containerRect: DOMRect;
    let scrollTop: number;

    if (actualScrollElement === window) {
      scrollTop = window.pageYOffset;
      containerRect = container.getBoundingClientRect();
    } else {
      const scrollEl = actualScrollElement as HTMLElement;
      scrollTop = scrollEl.scrollTop;
      containerRect = container.getBoundingClientRect();
      const scrollElRect = scrollEl.getBoundingClientRect();

      // Adjust container position relative to scroll container
      containerRect = {
        ...containerRect,
        top: containerRect.top - scrollElRect.top + scrollTop,
        bottom: containerRect.bottom - scrollElRect.top + scrollTop,
        left: containerRect.left - scrollElRect.left,
        right: containerRect.right - scrollElRect.left,
      } as DOMRect;
    }

    const dimensions: CachedDimensions = {
      containerTop:
        actualScrollElement === window ? containerRect.top + scrollTop : containerRect.top,
      containerBottom:
        actualScrollElement === window ? containerRect.bottom + scrollTop : containerRect.bottom,
      containerLeft: containerRect.left,
      containerRight: containerRect.right,
      elementWidth: stickyElement.offsetWidth,
      elementHeight: stickyElement.offsetHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      timestamp: now,
    };

    dimensionsCache.current = dimensions;
    return dimensions;
  }, [scrollContainer, isClient]);

  // Invalidate cache on resize
  const invalidateCache = useCallback(() => {
    dimensionsCache.current = null;
  }, []);

  // Optimized position handler with memoized calculations
  const handlePosition = useCallback(() => {
    if (
      !enabled ||
      !isClient ||
      typeof window === 'undefined' ||
      window.innerWidth < minScreenWidth
    ) {
      return;
    }

    if (!stickyRef.current || !containerRef.current) return;

    // Early check for scroll container - match Embarc's approach
    let actualScrollElement: HTMLElement | Window;
    if (scrollContainer) {
      if (typeof scrollContainer === 'string') {
        const element = document.querySelector(scrollContainer) as HTMLElement;
        if (!element) {
          // Element not found yet, skip positioning
          return;
        }
        actualScrollElement = element;
      } else {
        actualScrollElement = scrollContainer;
      }
    } else {
      actualScrollElement = window;
    }

    const dimensions = getDimensions();
    if (!dimensions) return;

    const stickyElement = stickyRef.current!;
    const {
      containerTop,
      containerBottom,
      containerLeft,
      containerRight,
      elementWidth,
      elementHeight,
    } = dimensions;

    // Calculate scroll position
    const scrollTop =
      actualScrollElement === window
        ? window.pageYOffset
        : (actualScrollElement as HTMLElement).scrollTop;
    const viewportTop = scrollTop;

    // Three-state logic with memoized calculations
    let targetState: StickyState = 'static';
    const scrollPosition = viewportTop + stickyOffset;
    const scrollPositionWithHeight = scrollPosition + elementHeight;

    if (scrollPosition < containerTop) {
      targetState = 'static';
    } else if (scrollPositionWithHeight <= containerBottom) {
      targetState = 'fixed';
    } else {
      targetState = 'bottom';
    }

    // Only apply changes if state actually changed (performance optimization)
    if (currentState.current !== targetState) {
      currentState.current = targetState;

      if (targetState === 'fixed') {
        // Get natural position by temporarily resetting
        stickyElement.style.position = 'static';
        const naturalRect = stickyElement.getBoundingClientRect();
        const container = containerRef.current!;
        const containerRect = container.getBoundingClientRect();

        let topPosition: number;
        let leftPosition: number;
        let width: string;

        if (actualScrollElement === window) {
          // Regular page positioning
          topPosition = stickyOffset;
          leftPosition = naturalRect.left;
          width = fixedWidth ? `${fixedWidth}px` : `${naturalRect.width}px`;
        } else {
          // Modal/container positioning - preserve natural width and position
          const scrollEl = actualScrollElement as HTMLElement;
          const scrollElRect = scrollEl.getBoundingClientRect();

          // Use the natural position and width to preserve layout
          topPosition = scrollElRect.top + stickyOffset;
          leftPosition = naturalRect.left; // Preserve natural left position
          width = fixedWidth ? `${fixedWidth}px` : `${naturalRect.width}px`; // Preserve natural width
        }

        // Apply fixed positioning
        stickyElement.style.position = 'fixed';
        stickyElement.style.top = `${topPosition}px`;
        stickyElement.style.left = `${leftPosition}px`;
        stickyElement.style.width = width;
        stickyElement.style.bottom = 'auto';
        stickyElement.style.right = 'auto';
        stickyElement.style.maxHeight = `${maxHeight}px`;
        stickyElement.style.overflowY = 'auto';
        stickyElement.style.zIndex = actualScrollElement === window ? '10' : '60';

        // Preserve shadows for right side only (price section)
        if (side === 'right') {
          stickyElement.style.boxShadow = '0px 2px 6px rgba(0,0,0,0.15)';
        } else {
          // Left side (image) - no shadow to match the border-only design
          stickyElement.style.boxShadow = '';
        }

        setIsFixed(true);
      } else if (targetState === 'bottom') {
        // Make container relative for absolute positioning
        const container = containerRef.current!;
        container.style.position = 'relative';

        // Get natural position and width by temporarily resetting
        stickyElement.style.position = 'static';
        const naturalRect = stickyElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        let bottomPosition: string;
        let leftPosition: string;
        let width: string;
        let zIndex: string;

        if (actualScrollElement === window) {
          // Page context - use simple positioning
          const leftOffset = naturalRect.left - containerRect.left;
          bottomPosition = '0';
          leftPosition = `${leftOffset}px`;
          width = fixedWidth ? `${fixedWidth}px` : `${naturalRect.width}px`;
          zIndex = '10';
        } else {
          // Modal context - preserve natural position and width
          const leftOffset = naturalRect.left - containerRect.left;
          bottomPosition = '0';
          leftPosition = `${leftOffset}px`; // Preserve natural left offset
          width = fixedWidth ? `${fixedWidth}px` : `${naturalRect.width}px`; // Preserve natural width
          zIndex = '60';
        }

        // Disable transitions to prevent drift
        stickyElement.style.transition = 'none';

        // Position absolutely at bottom of container
        stickyElement.style.position = 'absolute';
        stickyElement.style.top = 'auto';
        stickyElement.style.bottom = bottomPosition;
        stickyElement.style.left = leftPosition;
        stickyElement.style.right = 'auto';
        stickyElement.style.width = width;
        stickyElement.style.maxHeight = `${maxHeight}px`;
        stickyElement.style.overflowY = 'auto';
        stickyElement.style.zIndex = zIndex;

        // Preserve shadows for right side only (price section)
        if (side === 'right') {
          stickyElement.style.boxShadow = '0px 2px 6px rgba(0,0,0,0.15)';
        } else {
          // Left side (image) - no shadow to match the border-only design
          stickyElement.style.boxShadow = '';
        }

        setIsFixed(true);
      } else {
        // Reset to normal positioning
        stickyElement.style.position = '';
        stickyElement.style.top = '';
        stickyElement.style.bottom = '';
        stickyElement.style.left = '';
        stickyElement.style.right = '';
        stickyElement.style.width = '';
        stickyElement.style.maxHeight = '';
        stickyElement.style.overflowY = '';
        stickyElement.style.zIndex = '';
        stickyElement.style.boxShadow = '';
        stickyElement.style.transition = '';

        // Reset container positioning
        if (containerRef.current) {
          containerRef.current.style.position = '';
        }

        setIsFixed(false);
      }
    }
  }, [
    enabled,
    minScreenWidth,
    getDimensions,
    scrollContainer,
    stickyOffset,
    maxHeight,
    fixedWidth,
    side,
    isClient,
  ]);

  useEffect(() => {
    if (!enabled || !isClient) return;

    // Determine scroll container - match Embarc's approach
    let actualScrollElement: HTMLElement | Window;
    if (scrollContainer) {
      if (typeof scrollContainer === 'string') {
        const element = document.querySelector(scrollContainer) as HTMLElement;
        if (!element) {
          // Element not found yet, wait for it to appear - retry after a short delay
          const timeout = setTimeout(() => {
            // Retry by triggering a re-render
            handlePosition();
          }, 100);
          return () => clearTimeout(timeout);
        }
        actualScrollElement = element;
      } else {
        actualScrollElement = scrollContainer;
      }
    } else {
      actualScrollElement = window;
    }

    // Throttled scroll handler with requestAnimationFrame
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handlePosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Resize handler that invalidates cache and repositions
    const handleResize = () => {
      invalidateCache();
      handlePosition();
    };

    // Event listeners
    if (actualScrollElement === window) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      (actualScrollElement as HTMLElement).addEventListener('scroll', handleScroll, {
        passive: true,
      });
    }

    window.addEventListener('resize', handleResize);

    // Initial positioning
    handlePosition();

    return () => {
      // Cleanup event listeners
      if (actualScrollElement === window) {
        window.removeEventListener('scroll', handleScroll);
      } else {
        (actualScrollElement as HTMLElement).removeEventListener('scroll', handleScroll);
      }

      window.removeEventListener('resize', handleResize);

      // Cleanup positioning on unmount
      if (stickyRef.current) {
        const element = stickyRef.current;
        element.style.position = '';
        element.style.top = '';
        element.style.bottom = '';
        element.style.left = '';
        element.style.right = '';
        element.style.width = '';
        element.style.maxHeight = '';
        element.style.overflowY = '';
        element.style.zIndex = '';
        element.style.boxShadow = '';
        element.style.transition = '';
      }

      // Reset container positioning
      if (containerRef.current) {
        containerRef.current.style.position = '';
      }

      // Reset state tracking
      currentState.current = 'static';
      setIsFixed(false);
      dimensionsCache.current = null;
    };
  }, [enabled, handlePosition, invalidateCache, scrollContainer, isClient]);

  return {
    stickyRef,
    containerRef,
    isFixed,
  };
}
